import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import { Close, Fullscreen, FullscreenExit } from '@mui/icons-material';
import SimpleDicomViewer from './SimpleDicomViewer';

const SimpleDicomViewerDialog = ({ 
  open, 
  onClose, 
  dicomFile, 
  patientInfo = {}, 
  studyInfo = {},
  title = "Radiology Viewer",
  readOnly = false
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [dicomMetadata, setDicomMetadata] = useState(null);

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleClose = () => {
    setIsFullscreen(false);
    setViewerError(null);
    setDicomMetadata(null);
    onClose();
  };

  const handleViewerError = (error) => {
    console.error('Radiology viewer error:', error);
    setViewerError(error);
  };

  const handleMetadataExtracted = (metadata) => {
    setDicomMetadata(metadata);
  };

  if (!dicomFile) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullScreen={isFullscreen}
      disableRestoreFocus
      aria-labelledby="simple-dicom-viewer-dialog-title"
      aria-describedby="simple-dicom-viewer-dialog-description"
      PaperProps={{
        sx: {
          width: isFullscreen ? '100vw' : '95vw',
          height: isFullscreen ? '100vh' : '90vh',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: isFullscreen ? 0 : 2,
        }
      }}
    >
      <DialogTitle 
        id="simple-dicom-viewer-dialog-title"
        sx={{
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {patientInfo.name && (
            <Typography variant="body2" color="text.secondary">
              Patient: {patientInfo.name} {patientInfo.id && `(ID: ${patientInfo.id})`}
            </Typography>
          )}
          {studyInfo.description && (
            <Typography variant="body2" color="text.secondary">
              Study: {studyInfo.description}
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleFullscreen}
            aria-label={isFullscreen ? "exit fullscreen" : "fullscreen"}
          >
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent id="simple-dicom-viewer-dialog-description" sx={{ p: 0, flex: 1 }}>
        {viewerError && (
          <Alert severity="error" sx={{ m: 2 }}>
            <Typography variant="body2">
              <strong>Error:</strong> {viewerError}
            </Typography>
          </Alert>
        )}
        
        <Box sx={{ height: '100%' }}>
          <SimpleDicomViewer
            dicomFile={dicomFile}
            patientInfo={patientInfo}
            studyInfo={studyInfo}
            onError={handleViewerError}
            onMetadataExtracted={handleMetadataExtracted}
            readOnly={readOnly}
          />
        </Box>
      </DialogContent>

      {dicomMetadata && (
        <DialogActions sx={{ 
          justifyContent: 'space-between',
          px: 2,
          pb: 2
        }}>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`${dicomMetadata.columns || dicomMetadata.width} × ${dicomMetadata.rows || dicomMetadata.height}`} 
              size="small" 
              color="primary"
            />
            {dicomMetadata.modality && dicomMetadata.modality !== 'Unknown' && (
              <Chip 
                label={dicomMetadata.modality} 
                size="small" 
                color="secondary"
              />
            )}
            {dicomMetadata.photometricInterpretation && (
              <Chip 
                label={dicomMetadata.photometricInterpretation} 
                size="small" 
                variant="outlined"
              />
            )}
          </Stack>
          
          <Button onClick={handleClose} variant="contained" autoFocus>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SimpleDicomViewerDialog; 